import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, AlertTriangle, MessageCircle, Loader2, RefreshCw, Send, Radio, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const AIDiagnosticsPage = ({ isAuthenticated, currentUser }) => {
  const API = getBackendUrl();

  // Yellow-stuck summary state
  const [yellowLoading, setYellowLoading] = useState(false);
  const [yellowSummary, setYellowSummary] = useState(null);

  // Model health status
  const [modelStatus, setModelStatus] = useState(null);

  // Admin Q&A state
  const [qaSessionId, setQaSessionId] = useState(null);
  const [qaMessages, setQaMessages] = useState([]);
  const [qaInput, setQaInput] = useState('');
  const [qaLoading, setQaLoading] = useState(false);
  const qaScrollRef = useRef(null);

  // Station anomaly state
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState('');
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalyResult, setAnomalyResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || currentUser?.role !== 'admin') return;
    const token = storage.getItem('token');
    // Dedicated admin endpoint — returns {stations: [{id, name}]} sorted alphabetically by name.
    axios.get(`${API}/api/admin/ai-diagnostics/stations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStations(r.data?.stations || []))
      .catch(() => setStations([]));
    // Poll LLM model health every 30s
    const fetchStatus = () => {
      axios.get(`${API}/api/admin/ai-diagnostics/model-status`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => setModelStatus(r.data)).catch(() => {});
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    return () => clearInterval(iv);
  }, [API, isAuthenticated, currentUser]);

  useEffect(() => {
    if (qaScrollRef.current) {
      qaScrollRef.current.scrollTop = qaScrollRef.current.scrollHeight;
    }
  }, [qaMessages]);

  if (!isAuthenticated || currentUser?.role !== 'admin') {
    return <Navigate to="/login?redirect=/admin/ai-diagnostics" replace />;
  }

  const runYellowSummary = async () => {
    setYellowLoading(true);
    setYellowSummary(null);
    try {
      const token = storage.getItem('token');
      const r = await axios.post(
        `${API}/api/admin/ai-diagnostics/yellow-stuck-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 120000 }
      );
      setYellowSummary(r.data);
    } catch (e) {
      toast.error('Σφάλμα: ' + (e.response?.data?.detail || e.message));
    } finally {
      setYellowLoading(false);
    }
  };

  const askQuestion = async () => {
    if (!qaInput.trim()) return;
    const question = qaInput.trim();
    setQaInput('');
    setQaMessages(m => [...m, { role: 'user', text: question }]);
    setQaLoading(true);
    try {
      const token = storage.getItem('token');
      const r = await axios.post(
        `${API}/api/admin/ai-diagnostics/ask`,
        { session_id: qaSessionId, question, include_snapshot: true },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 120000 }
      );
      setQaSessionId(r.data.session_id);
      setQaMessages(m => [...m, { role: 'assistant', text: r.data.answer, snapshot: r.data.snapshot }]);
    } catch (e) {
      setQaMessages(m => [...m, { role: 'assistant', text: '⚠️ ' + (e.response?.data?.detail || e.message), error: true }]);
    } finally {
      setQaLoading(false);
    }
  };

  const runAnomaly = async () => {
    if (!selectedStation) {
      toast.error('Επίλεξε σταθμό πρώτα');
      return;
    }
    setAnomalyLoading(true);
    setAnomalyResult(null);
    try {
      const token = storage.getItem('token');
      const r = await axios.post(
        `${API}/api/admin/ai-diagnostics/station-anomaly/${selectedStation}`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 120000 }
      );
      setAnomalyResult(r.data);
    } catch (e) {
      toast.error('Σφάλμα: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAnomalyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0730] via-[#2D0C4B] to-[#1A0730] py-8 px-4" data-testid="ai-diagnostics-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">AI Diagnostics</h1>
            <p className="text-sm text-purple-200">GPT-5.6 Luna · Ελληνικά · Admin only</p>
          </div>
        </div>

        {/* Model Health Banner */}
        {modelStatus && (
          <div
            className={`rounded-xl p-4 border ${
              modelStatus.health.state === 'healthy'
                ? 'bg-green-500/10 border-green-500/30'
                : modelStatus.health.state === 'degraded'
                ? 'bg-yellow-500/10 border-yellow-500/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}
            data-testid="model-status-banner"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">
                  {modelStatus.health.state === 'healthy' ? '🟢' : modelStatus.health.state === 'degraded' ? '🟡' : '🔴'}
                </span>
                <div>
                  <div className="text-white font-semibold text-sm">
                    Primary: <code className="text-purple-200">{modelStatus.health.primary_model}</code>
                    {modelStatus.health.fallback_model && (
                      <>
                        <span className="mx-2 text-white/40">→ Fallback:</span>
                        <code className="text-purple-200">{modelStatus.health.fallback_model}</code>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-white/60">
                    {modelStatus.health.state === 'healthy'
                      ? `✓ ${modelStatus.health.success_count}/${modelStatus.health.window_size} επιτυχείς κλήσεις`
                      : modelStatus.health.state === 'degraded'
                      ? `⚠️ Το fallback έχει ενεργοποιηθεί ${modelStatus.health.fallback_hits} φορές. Το primary model πιθανώς έχει πρόβλημα.`
                      : `🚨 ${modelStatus.health.failure_count} αποτυχίες! Άλλαξε το AI_LLM_MODEL στο backend/.env`}
                  </div>
                </div>
              </div>
              {modelStatus.health.last_failure && (
                <details className="text-xs text-white/70">
                  <summary className="cursor-pointer hover:text-white">Τελευταίο σφάλμα ▾</summary>
                  <div className="mt-2 p-2 bg-black/30 rounded text-white/80 max-w-md">
                    <div><strong>Model:</strong> {modelStatus.health.last_failure.model}</div>
                    <div><strong>Purpose:</strong> {modelStatus.health.last_failure.purpose}</div>
                    <div className="break-words"><strong>Error:</strong> {modelStatus.health.last_failure.error}</div>
                    <div className="text-white/50 mt-1">
                      Για διόρθωση: επεξεργάσου <code>backend/.env</code> → άλλαξε το <code>AI_LLM_MODEL</code> σε ένα διαθέσιμο model
                      (π.χ. gpt-5.6-terra, gpt-5.6-sol, gpt-5.4) και κάνε restart backend.
                    </div>
                  </div>
                </details>
              )}
            </div>
          </div>
        )}

        {/* Yellow-stuck Summarizer */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6" data-testid="yellow-stuck-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <h2 className="text-xl font-bold text-white">Yellow-Stuck Summary</h2>
              <Badge className="bg-yellow-500/20 text-yellow-100">on-demand</Badge>
            </div>
            <Button
              onClick={runYellowSummary}
              disabled={yellowLoading}
              className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white"
              data-testid="yellow-stuck-btn"
            >
              {yellowLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {yellowLoading ? 'Παράγεται...' : 'Summarize'}
            </Button>
          </div>
          {yellowSummary && (
            <div data-testid="yellow-stuck-result" className="space-y-2">
              <div className="flex gap-2 text-xs text-purple-200">
                <Badge className="bg-purple-500/20 text-purple-100">Stuck count: {yellowSummary.stuck_count ?? 'n/a'}</Badge>
                <span className="text-white/50">Παράχθηκε: {new Date(yellowSummary.generated_at).toLocaleTimeString('el-GR')}</span>
              </div>
              <div className="whitespace-pre-wrap text-white/90 bg-black/30 rounded-lg p-4 border border-white/10 text-sm leading-relaxed">
                {yellowSummary.raw_summary}
              </div>
            </div>
          )}
          {!yellowSummary && !yellowLoading && (
            <p className="text-white/50 text-sm">Πάτα Summarize για ζωντανή διάγνωση των stuck σταθμών.</p>
          )}
        </Card>

        {/* Station Anomaly */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6" data-testid="station-anomaly-card">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-5 h-5 text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Station Anomaly Narrative</h2>
            <Badge className="bg-cyan-500/20 text-cyan-100">per station</Badge>
          </div>
          <div className="flex gap-2 mb-3">
            <select
              value={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
              className="flex-1 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="station-select"
            >
              <option value="" className="bg-gray-900 text-white">-- Επίλεξε σταθμό --</option>
              {stations.map(s => (
                <option key={s.id || s._id} value={s.id || s._id} className="bg-gray-900 text-white">{s.name}</option>
              ))}
            </select>
            <Button
              onClick={runAnomaly}
              disabled={anomalyLoading || !selectedStation}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
              data-testid="station-anomaly-btn"
            >
              {anomalyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Analyze
            </Button>
          </div>
          {anomalyResult && (
            <div data-testid="anomaly-result" className="space-y-2">
              <div className="text-sm text-purple-200">
                <strong className="text-white">{anomalyResult.station_name}</strong>
                <span className="text-white/50 ml-2">· {new Date(anomalyResult.generated_at).toLocaleTimeString('el-GR')}</span>
              </div>
              <div className="whitespace-pre-wrap text-white/90 bg-black/30 rounded-lg p-4 border border-white/10 text-sm leading-relaxed">
                {anomalyResult.narrative}
              </div>
            </div>
          )}
        </Card>

        {/* Admin Q&A */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6" data-testid="admin-qa-card">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-5 h-5 text-green-400" />
            <h2 className="text-xl font-bold text-white">Ask AI</h2>
            <Badge className="bg-green-500/20 text-green-100">multi-turn</Badge>
            {qaSessionId && <span className="text-xs text-white/50">session: {qaSessionId.slice(0, 8)}</span>}
          </div>
          <div
            ref={qaScrollRef}
            className="h-80 overflow-y-auto bg-black/30 rounded-lg p-4 border border-white/10 mb-3 space-y-3"
            data-testid="qa-history"
          >
            {qaMessages.length === 0 && (
              <p className="text-white/50 text-sm text-center py-8">
                Γράψε ερώτηση, π.χ. "Πόσοι σταθμοί είναι ενεργοί;" ή "Ποιο είναι το πιο κοινό error τώρα;"
              </p>
            )}
            {qaMessages.map((m, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg text-sm whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-purple-600/30 text-white ml-8' : m.error ? 'bg-red-500/20 text-red-100 mr-8' : 'bg-white/5 text-white/90 mr-8'
                }`}
                data-testid={`qa-message-${i}`}
              >
                <div className="text-xs opacity-70 mb-1">{m.role === 'user' ? '👤 Εσύ' : '🤖 AI'}</div>
                {m.text}
              </div>
            ))}
            {qaLoading && (
              <div className="p-3 rounded-lg bg-white/5 text-white/70 text-sm mr-8 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Σκέφτεται...
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              value={qaInput}
              onChange={(e) => setQaInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askQuestion(); } }}
              placeholder="Γράψε ερώτηση στα Ελληνικά..."
              rows={2}
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40 flex-1"
              data-testid="qa-input"
              disabled={qaLoading}
            />
            <Button
              onClick={askQuestion}
              disabled={qaLoading || !qaInput.trim()}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white self-end"
              data-testid="qa-send-btn"
            >
              {qaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AIDiagnosticsPage;
